/**
 * AdminPanel — gestión básica de usuarios y roles del tenant (US-26).
 *
 * Sólo se llega aquí si el descriptor habilita el ítem de menú `admin`
 * (capabilities.menu, R-PT-02). Recordatorio R-PT-04: ocultar el panel es
 * cosmético; el kernel revalida el permiso admin en CADA endpoint. Si un
 * no-admin fuerza la llamada, el backend responde 403 y aquí se muestra el
 * estado de permiso denegado.
 *
 * Funciones (contra mock o kernel real, según VITE_USE_MOCK):
 *  - Listar empleados del tenant (nombre, email, rol, estado invitación).
 *  - Invitar empleado por email eligiendo rol inicial.
 *  - Cambiar el rol de un empleado existente.
 */

import { useState, useEffect, useCallback } from 'react'
import { useSession } from '../context/SessionContext.jsx'
import { listUsers, inviteUser, changeUserRole } from '../api/client.js'
import { ROLES, ROLE_LABELS, ESTADO_LABELS } from '../lib/contract.js'
import { messageForCode } from '../api/errors.js'
import { logEvent, logError } from '../lib/log.js'

function EstadoBadge({ estado }) {
  return (
    <span className={`admin-badge admin-badge--${estado}`}>
      {ESTADO_LABELS[estado] ?? estado}
    </span>
  )
}

function InviteForm({ onInvited }) {
  const [email, setEmail]     = useState('')
  const [rol, setRol]         = useState('usuario')
  const [sending, setSending] = useState(false)
  const [error, setError]     = useState(null)
  const [ok, setOk]           = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    setOk(null)
    setSending(true)
    try {
      const { user } = await inviteUser({ email: email.trim(), rol })
      logEvent('admin.invite.ok', { email: user.email, rol: user.rol })
      setOk(`Invitación enviada a ${user.email} (${ROLE_LABELS[user.rol]}). Estado: pendiente.`)
      setEmail('')
      setRol('usuario')
      onInvited(user)
    } catch (err) {
      logError('admin.invite.error', { code: err.code, status: err.status })
      setError(messageForCode(err.code, err.message))
    } finally {
      setSending(false)
    }
  }

  return (
    <form className="admin-invite" onSubmit={submit}>
      <h3 className="admin-invite__title">Invitar empleado</h3>
      <div className="admin-invite__row">
        <input
          type="email"
          className="admin-input"
          placeholder="email@empresa.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          disabled={sending}
          aria-label="Email del empleado"
        />
        <select
          className="admin-select"
          value={rol}
          onChange={e => setRol(e.target.value)}
          disabled={sending}
          aria-label="Rol inicial"
        >
          {ROLES.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        <button className="admin-btn" type="submit" disabled={sending || !email.trim()}>
          {sending ? 'Enviando…' : 'Invitar'}
        </button>
      </div>
      {error && <p className="admin-msg admin-msg--error" role="alert">{error}</p>}
      {ok    && <p className="admin-msg admin-msg--ok" role="status">{ok}</p>}
    </form>
  )
}

function UserRow({ user, isSelf, onRoleChanged }) {
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)
  const [saved, setSaved]   = useState(false)

  const onChange = async (e) => {
    const nuevoRol = e.target.value
    const rolPrevio = user.rol
    if (nuevoRol === rolPrevio) return
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      const { user: actualizado } = await changeUserRole(user.id, nuevoRol)
      logEvent('admin.role_change.ok', { user_id: user.id, from: rolPrevio, to: actualizado.rol })
      onRoleChanged(actualizado)
      setSaved(true)
    } catch (err) {
      logError('admin.role_change.error', { user_id: user.id, code: err.code, status: err.status })
      setError(messageForCode(err.code, err.message))
    } finally {
      setSaving(false)
    }
  }

  return (
    <tr className={saving ? 'admin-row--saving' : undefined}>
      <td>{user.display_name}</td>
      <td className="admin-cell--muted">{user.email}</td>
      <td>
        <select
          className="admin-select admin-select--role"
          value={user.rol}
          onChange={onChange}
          disabled={saving || isSelf}
          title={isSelf ? 'No puedes cambiar tu propio rol' : undefined}
          aria-label={`Rol de ${user.display_name}`}
        >
          {ROLES.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        {saving && <span className="admin-inline-note">Guardando…</span>}
        {saved  && !saving && <span className="admin-inline-note admin-inline-note--ok">Guardado</span>}
        {error  && <span className="admin-inline-note admin-inline-note--error" role="alert">{error}</span>}
      </td>
      <td><EstadoBadge estado={user.estado} /></td>
    </tr>
  )
}

export default function AdminPanel() {
  const { user } = useSession()
  const [users, setUsers]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { users } = await listUsers()
      logEvent('admin.list.ok', { count: users.length })
      setUsers(users)
    } catch (err) {
      logError('admin.list.error', { code: err.code, status: err.status })
      setError(messageForCode(err.code, err.message))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleInvited = useCallback((nuevo) => {
    setUsers(prev => [...prev, nuevo])
  }, [])

  const handleRoleChanged = useCallback((actualizado) => {
    setUsers(prev => prev.map(u => (u.id === actualizado.id ? { ...u, ...actualizado } : u)))
  }, [])

  return (
    <section className="admin-panel">
      <header className="admin-panel__head">
        <h2 className="admin-panel__title">Administración de usuarios</h2>
        <p className="admin-panel__subtitle">
          Empleados del tenant, sus roles y el estado de su invitación.
        </p>
      </header>

      <InviteForm onInvited={handleInvited} />

      {loading && <p className="admin-state">Cargando empleados…</p>}

      {error && !loading && (
        <div className="admin-state admin-state--error" role="alert">
          <p>{error}</p>
          <button className="admin-btn admin-btn--ghost" onClick={load}>Reintentar</button>
        </div>
      )}

      {!loading && !error && (
        users.length === 0 ? (
          <p className="admin-state">Todavía no hay empleados en este tenant.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === user?.id}
                  onRoleChanged={handleRoleChanged}
                />
              ))}
            </tbody>
          </table>
        )
      )}
    </section>
  )
}
